import dayjs from 'dayjs'
import { upperFirst } from 'lodash-es'

const VOWELS = /^[aeiou]/i

// Currency formatters
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

const currencyFormatterDecimalsNoMinNoMax = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 20
})

const currencyFormatterForceTwoDecimalsNoMax = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 20
})

const currencyFormatterForceTwoDecimalsMinAndMax = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

// Currency formatting functions
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return null
  return currencyFormatter.format(value)
}

export const formatCurrencyWithOptions = (value, forceTwoDecimals = false, noMinNoMax = false, formatTwoDecimals = false) => {
  if (value === null || value === undefined || value === '') return null

  if (noMinNoMax) return currencyFormatterDecimalsNoMinNoMax.format(value)
  if (forceTwoDecimals) return currencyFormatterForceTwoDecimalsNoMax.format(value)
  if (formatTwoDecimals) return currencyFormatterForceTwoDecimalsMinAndMax.format(value)
  return currencyFormatter.format(value)
}

export const unformatCurrency = (value) => {
  return Number(value.replace(/[^0-9.-]+/g, ''))
}

// Date formatting functions
function executeDateFilter (value, format) {
  try {
    const date = dayjs(value)
    if (!date.isValid()) return ''
    return date.format(format)
  } catch (err) {
    return ''
  }
}

export const formatPrettyDate = (value) => {
  return executeDateFilter(value, 'dddd, MMM D, YYYY')
}

export const formatPrettyDateWithFullMonth = (value) => {
  return executeDateFilter(value, 'dddd, MMMM D, YYYY')
}

export const formatShortDate = (value) => {
  return executeDateFilter(value, 'MM/DD/YYYY')
}

export const formatShortDateWithTime = (value) => {
  return executeDateFilter(value, 'MM/DD h:mm A')
}

export const formatTime = (value) => {
  return executeDateFilter(value, 'h:mm A')
}

export const formatShortPrettyDate = (value) => {
  return executeDateFilter(value, 'MMM D, YYYY')
}

export const formatShortDateWith12HourTime = (value) => {
  return executeDateFilter(value, 'MM/DD/YYYY h:mm A')
}

export const formatShortDateWith12HourTimeAndZone = (value) => {
  const formattedDate = executeDateFilter(value, 'MM/DD/YY, h:mm A')
  if (!formattedDate) return ''

  const shortTimeZone = new Date(value).toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ')[2]
  return shortTimeZone ? `${formattedDate} ${shortTimeZone}` : formattedDate
}

// Number formatting functions
export const formatNumberWithCommas = (value) => {
  const parts = value.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

// Text formatting functions
export const formatAOrAn = (str, shouldUppercase = false) => {
  let indefiniteArticle = 'a'

  if (VOWELS.test(str)) {
    indefiniteArticle = 'an'
  }

  if (shouldUppercase) {
    indefiniteArticle = upperFirst(indefiniteArticle)
  }

  return indefiniteArticle
}

// Phone number formatting
export const formatPhone = (number) => {
  if (number === null) return null

  const digits = number.match(/\d+/g)
  if (digits === null) return null

  let numericValue = digits.join('')
  let formattedString = ''

  // Area code
  if (numericValue.length >= 3) formattedString += '(' + numericValue.substring(0, 3) + ')'
  else formattedString = numericValue
  numericValue = numericValue.substring(3)

  // First three digits
  if (numericValue.length >= 4) formattedString += ' ' + numericValue.substring(0, 3) + '-'
  else formattedString += ' ' + numericValue
  numericValue = numericValue.substring(3)

  // Next four digits
  formattedString += numericValue.substring(0, 4)
  numericValue = numericValue.substring(4)

  // Remaining digits
  if (numericValue.length > 0) formattedString += ' ext. ' + numericValue

  return formattedString.trim()
}

// URL formatting functions
export const stripUtmString = (url) => {
  const parser = url.split('?')
  if (parser[1]) {
    const params = parser[1].split('&')
    let queryString = ''
    for (let i = 0; i < params.length; i++) {
      const pair = params[i].split('=')
      if (!pair[0].includes('utm_')) { // removing any utm_ queries
        if (queryString === '') {
          queryString += '?' + pair[0] + '=' + decodeURIComponent(pair[1])// sanitize query values
        } else {
          queryString += '&' + pair[0] + '=' + decodeURIComponent(pair[1])// sanitize query values
        }
      }
    }
    return parser[0] + queryString
  }
  return url
}

export const cleanUrl = (baseUrl, urlPath) => {
  const base = baseUrl.trim().replace(/\/$/, '')
  const path = urlPath.trim().replace(/^\//, '')
  return `${base}/${path}`
}

// Date calculation functions
export const calculateMonthlyDuration = (startDate, endDate) => {
  const startDateObj = dayjs(startDate)
  const endDateObj = dayjs(endDate).add(1, 'day')
  const duration = endDateObj.diff(startDateObj, 'months', true)

  if (duration < 0) return duration

  const decimal = duration - Math.floor(duration)
  if (decimal <= 0.1) return Math.floor(duration)
  else if (decimal >= 0.9) return Math.ceil(duration)

  return Math.max(duration, 1)
}

// Google address formatting
export const formatGoogleAddress = (address, clientName, latlngFunc = true) => {
  const addressComponents = {
    formatted: address.formatted_address,
    googlePlaceId: address.place_id,
    lat: latlngFunc ? address.geometry.location.lat() : address.geometry.location.lat,
    lng: latlngFunc ? address.geometry.location.lng() : address.geometry.location.lng,
    name: (
      address.types !== undefined && (address.types.includes('street_address') ||
      address.types.includes('premise'))
    ) && clientName
      ? clientName
      : address.name
  }
  address.address_components.forEach((component) => {
    if (component.types.includes('street_number')) {
      addressComponents.streetNumber = component.long_name
    } else if (component.types.includes('route')) {
      addressComponents.street = component.long_name
    } else if (component.types.includes('subpremise')) {
      addressComponents.subpremise = component.long_name
    } else if (component.types.includes('locality')) {
      addressComponents.city = component.long_name
    } else if (component.types.includes('sublocality_level_1') && !addressComponents.city) {
      addressComponents.city = component.long_name
    } else if (component.types.includes('administrative_area_level_1')) {
      addressComponents.state = component.short_name
    } else if (component.types.includes('postal_code')) {
      addressComponents.zip = component.long_name
    } else if (component.types.includes('country')) {
      addressComponents.country = component.short_name
    }
  })

  return addressComponents
}
